import { on } from '@ngrx/store';

import { createImmutableReducer } from '../../base/store/helpers';
import BetslipPickActions from '../picks/actions';
import { addGroup, replaceGroup } from '../picks/grouped-picks.actions';
import { pickSelectionHandlers } from '../picks/reducer';
import { addOrderedPicks, removeOrderedTypePicks } from '../picks/services/type-pick.utils';
import BetslipSystemBetActions from './actions';
import { ISystemBetPickState, ISystemBetState, ISystemBetTypeStake, defaultSystemBetState } from './state';

export const systemBetReducer = createImmutableReducer(
    defaultSystemBetState,
    ...pickSelectionHandlers<ISystemBetState>(),
    on(BetslipSystemBetActions.addPick, (state, action) => {
        state.picks[action.pickId] = {
            ...getDefaultPickState(),
            ...action.pickState,
        };

        return state;
    }),
    on(BetslipSystemBetActions.updateOrder, (state, action) => {
        if (state.picks[action.pickId]) {
            state.picks[action.pickId].order = action.order;
        }

        return state;
    }),

    on(BetslipSystemBetActions.removePick, (state, action) => {
        delete state.picks[action.pickId];

        return state;
    }),
    on(BetslipSystemBetActions.toggleSystemBetPickBanker, (state, action) => {
        const pick = state.picks[action.pickId];
        pick.isBanker = !pick.isBanker;

        return state;
    }),
    on(BetslipSystemBetActions.setSystemType, (state, action) => {
        state.systemInfo = action.systemType;

        return state;
    }),
    on(BetslipSystemBetActions.setSystemBetStake, (state, action) => {
        state.stake = action.stake;
        state.actualStake = action.stakeStr ? action.stakeStr : (action.stake?.toString() ?? null);

        return state;
    }),
    on(BetslipSystemBetActions.toggleSystemBanker, (state) => {
        state.isBankerActive = !state.isBankerActive;
        Object.values(state.picks).forEach((pick) => {
            pick.isBanker = false;
        });

        return state;
    }),
    on(BetslipSystemBetActions.setSystemEachWay, (state, action) => {
        state.isEachWay = action.isEachWay;

        return state;
    }),
    on(BetslipSystemBetActions.updateSystemBetPickState, (state, action) => {
        const pick = state.picks[action.pickId.toString()];
        if (!pick) {
            return state;
        }

        state.picks[action.pickId.toString()] = {
            ...pick,
            ...action.update,
        };

        return state;
    }),
    on(BetslipSystemBetActions.updateSystemTypeStakes, (state, action) => {
        const types = action.systemTypes;
        const existingTypeStakes = state.linearTypeStakes ?? {};
        const typeStakes: Record<string, ISystemBetTypeStake> = {};

        types.forEach(({ type, betCount }) => {
            const key = type.key;

            if (typeStakes[key]) {
                return;
            }

            const existingStake = existingTypeStakes[key];

            if (existingStake) {
                typeStakes[key] = existingStake;

                return;
            }

            typeStakes[key] = { actualStake: null, stake: null, slipType: type, isEachWay: false, betCount };
        });

        return {
            ...state,
            linearTypeStakes: typeStakes,
        };
    }),
    on(BetslipSystemBetActions.setSystemBetTypeStake, (state, action) => {
        const stake = action.stake;
        const actualStake = action.stakeStr ? action.stakeStr : (action.stake?.toString() ?? null);

        const typeKey = action.systemType.key;

        if (!state.linearTypeStakes) {
            return state;
        }

        state.linearTypeStakes[typeKey] = {
            stake,
            actualStake,
            slipType: action.systemType,
            betCount: action.betCount,
            isEachWay: state.linearTypeStakes[typeKey]?.isEachWay,
        };

        return state;
    }),
    on(BetslipSystemBetActions.removeSystemBetTypeStake, (state, action) => {
        const typeKey = action.systemTypeKey;

        if (!state.linearTypeStakes) {
            return state;
        }

        state.linearTypeStakes[typeKey] = {
            stake: null,
            actualStake: null,
            slipType: state.linearTypeStakes[typeKey].slipType,
            betCount: state.linearTypeStakes[typeKey].betCount,
            isEachWay: state.linearTypeStakes[typeKey]?.isEachWay,
        };

        return state;
    }),
    on(BetslipSystemBetActions.setSystemBetTypeEachWay, (state, action) => {
        const isEachWay = action.isEachWay;

        const typeKey = action.systemType.key;

        if (!state.linearTypeStakes) {
            return state;
        }

        const previousState = state.linearTypeStakes[typeKey];

        state.linearTypeStakes[typeKey] = {
            stake: previousState?.stake ?? 0,
            actualStake: previousState?.actualStake ?? null,
            betCount: previousState?.betCount ?? 0,
            slipType: action.systemType,
            isEachWay,
        };

        return state;
    }),
    on(BetslipSystemBetActions.setSystemBetTypeStakeValue, (state, action) => {
        const stake = action.stake;
        const actualStake = action.stakeStr ? action.stakeStr : (action.stake?.toString() ?? null);

        const typeKey = action.systemTypeKey;

        if (!state.linearTypeStakes?.[typeKey]) {
            return state;
        }

        state.linearTypeStakes[typeKey] = {
            ...state.linearTypeStakes[typeKey],
            stake,
            actualStake,
        };

        return state;
    }),

    on(BetslipSystemBetActions.toggleSystemBetPick, (state, action) => {
        const pickId = action.pickId.toString();
        if (!state.picks[pickId]) {
            return state;
        }
        state.picks[pickId].isSelected = !state.picks[pickId].isSelected;

        return state;
    }),
    on(BetslipSystemBetActions.toggleExpandLinearSystemBet, (state, action) => {
        state.isLinearExpanded = action.isExpanded;

        return state;
    }),
    on(replaceGroup, addGroup, (state, action) => {
        const legs = action.newPick.getLegs();

        addOrderedPicks(legs, state.picks, getDefaultPickState);

        return state;
    }),
    on(BetslipPickActions.removeTypePicks, (state, action) => {
        removeOrderedTypePicks(action.pickIds, state.picks);

        return state;
    }),
);

function getDefaultPickState(): ISystemBetPickState {
    return {
        isSelected: true,
        isBanker: false,
        isLocked: false,
        order: 0,
    };
}
